import { each, get, isEmpty, size } from 'lodash/fp';
import { array, number } from 'fp-ts';
import { TimeUnit } from '@mews/enums';
import { DENSE_LIGHT_THEME } from '@optimus-web/theme';
import { getToken } from '@optimus-web/core';
import {
    DATE_LEGEND_HEIGHT,
    TEXT_PADDING,
    FIRST_LINE,
    SECOND_LINE,
    DATE_LEGENT_COLUMN_FONT_SIZE,
    DATE_LEGENT_PARENT_FONT_SIZE,
    DATE_LEGEND_PARTS,
    EVENT_PADDING,
    EVENT_SIZE,
    EVENT_BOTTOM_POSITION,
    EVENT_TOP_POSITION,
} from '../../constants';
import { line, drawDayRectangle, drawText, drawEvents } from '../../utils/drawUtils';
import { generateColumnUnit, generateParentUnitPeriodLabel, getColumnWidthByTimeUnit } from '../../utils/timeUnit';
import { CursorPosition } from '../../types';
import { round } from '../../utils/mathUtils';

export default function DateLegend(
    getBgColor: $TSFixMe,
    localizeTimeUnitDate: $TSFixMe,
    indexToTimeUnitDateTime: $TSFixMe,
    indexToTimeUnitParentUnit: $TSFixMe
) {
    return {
        render({ ctx, xAxis, canvasWidth, canvasHeight, days, events, timeUnit, resourceLegendWidth }: $TSFixMe) {
            ctx.clearRect(0, 0, canvasWidth, DATE_LEGEND_HEIGHT);

            each((day: number) => {
                const startX: number = xAxis(day);
                const endX = xAxis(day + 1);
                const dayTime = indexToTimeUnitDateTime(day, timeUnit);
                const columnUnit = generateColumnUnit(dayTime, timeUnit);
                const dayRectangleHeight = DATE_LEGEND_HEIGHT / 2;
                drawDayRectangle(ctx, startX, endX - startX, dayRectangleHeight, getBgColor(day, timeUnit));
                drawText(ctx, columnUnit, startX + TEXT_PADDING, SECOND_LINE, true, DATE_LEGENT_COLUMN_FONT_SIZE);
                const dayEvents: Array<$TSFixMe> = get(day)(events);
                if (!isEmpty(dayEvents)) {
                    drawEvents(ctx, endX, size(dayEvents));
                }
            })(days);

            const lineColor = getToken('paletteBrandNight400')({ theme: DENSE_LIGHT_THEME });

            const displayedParentUnitRange = days.map((index: $TSFixMe) => indexToTimeUnitParentUnit(index, timeUnit));
            const parentUnitIndexes = days.map((index: $TSFixMe) =>
                displayedParentUnitRange.indexOf(indexToTimeUnitParentUnit(index, timeUnit))
            );
            const firstParentUnitOccurenceIndexes = array.uniq(number.Eq)(
                parentUnitIndexes,
                // @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 2.
                displayedParentUnitRange
            );

            each((parentIndex: number) => {
                const columnIndex = days[parentIndex];
                const columnXStart = xAxis(columnIndex);
                const columnIndexDateTime = indexToTimeUnitDateTime(columnIndex, timeUnit);
                const parentUnitLabel = generateParentUnitPeriodLabel(columnIndexDateTime, timeUnit);
                const parentUnitStartX = Math.max(columnXStart, resourceLegendWidth);

                ctx.fillStyle = getToken('paletteBrandNight0')({ theme: DENSE_LIGHT_THEME });
                ctx.fillRect(parentUnitStartX, 0, 500, DATE_LEGEND_HEIGHT / 2);
                line(ctx, columnXStart, 0, columnXStart, canvasHeight, lineColor);
                drawText(
                    ctx,
                    parentUnitLabel,
                    parentUnitStartX + TEXT_PADDING,
                    FIRST_LINE,
                    true,
                    DATE_LEGENT_PARENT_FONT_SIZE
                );
            })(firstParentUnitOccurenceIndexes);

            line(ctx, 0, DATE_LEGEND_HEIGHT / 2, canvasWidth, DATE_LEGEND_HEIGHT / 2, lineColor);

            line(ctx, 0, DATE_LEGEND_HEIGHT, canvasWidth, DATE_LEGEND_HEIGHT, lineColor);
        },
        collidesWithParentUnit(position: CursorPosition, resourceLegendWidth: number) {
            return position.y <= DATE_LEGEND_HEIGHT / 2 && position.x >= resourceLegendWidth;
        },
        collidesWithChildUnit(position: CursorPosition, resourceLegendWidth: number) {
            return position.y <= DATE_LEGEND_HEIGHT && position.x >= resourceLegendWidth;
        },
        collidesWithEvent(position: CursorPosition, xAxis: $TSFixMe, timeUnit: TimeUnit) {
            const columnWidth = getColumnWidthByTimeUnit(timeUnit);
            const mouseIndex = round(xAxis.invert(position.x));
            const xStartOfNextColumn = Number(xAxis(mouseIndex)) + columnWidth;

            const eventEndX = xStartOfNextColumn - EVENT_PADDING;
            const eventStartX = xStartOfNextColumn - EVENT_PADDING - EVENT_SIZE;

            return (
                position.x >= eventStartX &&
                position.x <= eventEndX &&
                position.y <= EVENT_BOTTOM_POSITION &&
                position.y >= EVENT_TOP_POSITION
            );
        },
        clicked(position: CursorPosition, resourceLegendWidth: number) {
            if (position.x <= resourceLegendWidth) return false;
            if (position.y <= DATE_LEGEND_HEIGHT / 2) return DATE_LEGEND_PARTS.PARENT_UNIT;
            if (position.y > DATE_LEGEND_HEIGHT / 2 && position.y <= DATE_LEGEND_HEIGHT) {
                return DATE_LEGEND_PARTS.COLUMN_UNIT;
            }
            return false;
        },
    };
}
